import React from 'react'
import { Theme } from '../types'
import { themeToStyleObject } from '../utils/themeToCSS'

interface AppPreviewProps {
  theme: Theme
  isDarkMode: boolean
}

export function AppPreview({ theme, isDarkMode }: AppPreviewProps) {
  const style = themeToStyleObject(theme, isDarkMode)

  return (
    <div
      className="min-h-full flex"
      style={{
        ...style,
        backgroundColor: 'var(--theme-background)',
        color: 'var(--theme-text)',
        fontFamily: 'var(--theme-body-font)',
        fontWeight: 'var(--theme-body-weight)' as any,
      }}
    >
      {/* Sidebar */}
      <aside
        className="w-64 flex-shrink-0 border-r"
        style={{
          backgroundColor: 'var(--theme-surface)',
          borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        }}
      >
        <div className="p-4 border-b" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
          <div
            className="text-xl"
            style={{
              fontFamily: 'var(--theme-display-font)',
              fontWeight: 'var(--theme-display-weight)' as any,
              color: 'var(--theme-primary)',
            }}
          >
            AppName
          </div>
        </div>

        <nav className="p-2">
          {[
            { icon: '📊', label: 'Dashboard', active: true },
            { icon: '👤', label: 'Users' },
            { icon: '📁', label: 'Projects' },
            { icon: '📈', label: 'Analytics' },
            { icon: '⚙️', label: 'Settings' },
          ].map((item, i) => (
            <a
              key={i}
              href="#"
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm mb-1"
              style={{
                backgroundColor: item.active ? 'var(--theme-primary)' : 'transparent',
                color: item.active ? 'white' : 'var(--theme-text-muted)',
                borderRadius: 'var(--theme-border-radius)',
              }}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </a>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-full">
        {/* Header */}
        <header
          className="h-16 flex items-center justify-between px-6 border-b"
          style={{
            backgroundColor: 'var(--theme-surface)',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          }}
        >
          <h1
            className="text-lg"
            style={{
              fontFamily: 'var(--theme-display-font)',
              fontWeight: 'var(--theme-display-weight)' as any,
            }}
          >
            Dashboard
          </h1>
          <div className="flex items-center gap-4">
            <button
              className="px-4 py-2 text-sm text-white"
              style={{
                backgroundColor: 'var(--theme-primary)',
                borderRadius: 'var(--theme-border-radius)',
              }}
            >
              + New Project
            </button>
            <div
              className="w-8 h-8 rounded-full"
              style={{ backgroundColor: 'var(--theme-secondary)' }}
            />
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-6 overflow-auto">
          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Revenue', value: '$48,294', change: '+12%' },
              { label: 'Active Users', value: '2,847', change: '+8%' },
              { label: 'Projects', value: '142', change: '+23%' },
              { label: 'Conversion', value: '3.24%', change: '-2%' },
            ].map((stat, i) => (
              <div
                key={i}
                className="p-5"
                style={{
                  backgroundColor: 'var(--theme-surface)',
                  borderRadius: 'var(--theme-border-radius)',
                  boxShadow: 'var(--theme-shadow)',
                }}
              >
                <div className="text-sm mb-1" style={{ color: 'var(--theme-text-muted)' }}>
                  {stat.label}
                </div>
                <div className="flex items-end justify-between">
                  <div
                    className="text-2xl"
                    style={{
                      fontFamily: 'var(--theme-display-font)',
                      fontWeight: 'var(--theme-display-weight)' as any,
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-sm"
                    style={{ color: stat.change.startsWith('+') ? '#22c55e' : '#ef4444' }}
                  >
                    {stat.change}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-6">
            {/* Main Content Card */}
            <div
              className="col-span-2 p-6"
              style={{
                backgroundColor: 'var(--theme-surface)',
                borderRadius: 'var(--theme-border-radius)',
                boxShadow: 'var(--theme-shadow)',
              }}
            >
              <h2
                className="text-lg mb-4"
                style={{
                  fontFamily: 'var(--theme-display-font)',
                  fontWeight: 'var(--theme-display-weight)' as any,
                }}
              >
                Recent Activity
              </h2>

              {/* Table */}
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ color: 'var(--theme-text-muted)' }}>
                    <th className="text-left pb-3 font-medium">Name</th>
                    <th className="text-left pb-3 font-medium">Status</th>
                    <th className="text-left pb-3 font-medium">Date</th>
                    <th className="text-right pb-3 font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'Project Alpha', status: 'Active', date: 'Jan 15', amount: '$2,500' },
                    { name: 'Marketing Campaign', status: 'Pending', date: 'Jan 14', amount: '$1,200' },
                    { name: 'Website Redesign', status: 'Completed', date: 'Jan 12', amount: '$8,400' },
                    { name: 'Mobile App', status: 'Active', date: 'Jan 10', amount: '$15,000' },
                  ].map((row, i) => (
                    <tr
                      key={i}
                      className="border-t"
                      style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}
                    >
                      <td className="py-3">{row.name}</td>
                      <td className="py-3">
                        <span
                          className="px-2 py-1 text-xs rounded-full"
                          style={{
                            backgroundColor:
                              row.status === 'Active' ? 'var(--theme-primary)' :
                              row.status === 'Pending' ? 'var(--theme-accent)' :
                              'var(--theme-secondary)',
                            color: 'white',
                            borderRadius: 'var(--theme-border-radius)',
                          }}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="py-3" style={{ color: 'var(--theme-text-muted)' }}>{row.date}</td>
                      <td className="py-3 text-right">{row.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Side Card - Form Example */}
            <div
              className="p-6"
              style={{
                backgroundColor: 'var(--theme-surface)',
                borderRadius: 'var(--theme-border-radius)',
                boxShadow: 'var(--theme-shadow)',
              }}
            >
              <h2
                className="text-lg mb-4"
                style={{
                  fontFamily: 'var(--theme-display-font)',
                  fontWeight: 'var(--theme-display-weight)' as any,
                }}
              >
                Quick Add
              </h2>

              <form className="space-y-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--theme-text-muted)' }}>
                    Project Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 text-sm border"
                    style={{
                      backgroundColor: 'var(--theme-background)',
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                      borderRadius: 'var(--theme-border-radius)',
                      color: 'var(--theme-text)',
                    }}
                    placeholder="Enter name..."
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--theme-text-muted)' }}>
                    Category
                  </label>
                  <select
                    className="w-full px-3 py-2 text-sm border"
                    style={{
                      backgroundColor: 'var(--theme-background)',
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                      borderRadius: 'var(--theme-border-radius)',
                      color: 'var(--theme-text)',
                    }}
                  >
                    <option>Design</option>
                    <option>Development</option>
                    <option>Marketing</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--theme-text-muted)' }}>
                    Description
                  </label>
                  <textarea
                    className="w-full px-3 py-2 text-sm border resize-none"
                    rows={3}
                    style={{
                      backgroundColor: 'var(--theme-background)',
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                      borderRadius: 'var(--theme-border-radius)',
                      color: 'var(--theme-text)',
                    }}
                    placeholder="Brief description..."
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    className="flex-1 px-4 py-2 text-sm text-white"
                    style={{
                      backgroundColor: 'var(--theme-primary)',
                      borderRadius: 'var(--theme-border-radius)',
                    }}
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm border"
                    style={{
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                      borderRadius: 'var(--theme-border-radius)',
                      color: 'var(--theme-text-muted)',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
